const UUID_REGEX =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** Matches a URL copied without its protocol, e.g. "sphere.holobuilder.com/..." */
const URL_WITHOUT_PROTOCOL_REGEX = /^[\w-]+(\.[\w-]+)+(:\d+)?\//;

/**
 * @param text The text to search for IDs
 * @returns The first ID found in the text, or undefined if there is none
 */
function getFirstId(text: string): string | undefined {
  return text.match(UUID_REGEX)?.[0];
}

/**
 * @param input The text to parse
 * @returns The input parsed as an http(s) URL, or undefined if it is not one
 */
function parseHttpUrl(input: string): URL | undefined {
  if (/\s/.test(input)) {
    return undefined;
  }

  const urlString = URL_WITHOUT_PROTOCOL_REGEX.test(input)
    ? `https://${input}`
    : input;

  try {
    const url = new URL(urlString);
    return ["http:", "https:"].includes(url.protocol) ? url : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Returns the query to search for from what the user typed or pasted.
 * If the input is a URL that contains an ID, only the ID is returned, so pasting
 * e.g. a Sphere workspace URL searches for the workspace ID.
 * In Sphere URLs the first ID in the path is the workspace, followed by e.g. a
 * group or project ID, so the first ID is used. IDs in the path are preferred
 * over IDs in the hash or the query string.
 *
 * @param input The text entered in the search bar
 * @returns The extracted ID, or the input unchanged if it is not a URL with an ID
 */
export function getSearchQueryFromInput(input: string): string {
  const url = parseHttpUrl(input.trim());
  if (!url) {
    return input;
  }

  return (
    getFirstId(url.pathname) ??
    getFirstId(url.hash) ??
    getFirstId(url.search) ??
    input
  );
}
